import { IMAGE_LOADER, ImageLoaderConfig, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, finalize, map, switchMap, take, takeWhile, tap, timer } from 'rxjs';
import { ApplicationStatusEnum, SocialChannelEnum } from '@/app/common/general-types/briefs.dto';
import {
  SubmittedPostDto,
  SubmittedPostStatusEnum,
} from '@/app/common/general-types/submitted-posts.dto';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { applicationStatusLabel } from '../admin-payout-labels/admin-payout-labels';
import {
  buildSubmittedPostChecklist,
  canCreateSubmittedPostOnChannel,
  canForceValidateSubmittedPost,
  canOfferCreateSubmittedPost,
  canRevalidateSubmittedPost,
  canUnhireApplication,
  canRestoreUnhiredToApplied,
  isStatusHistoryUserStatus,
  statusHistoryUserLabel,
  isSubmittedPostValidationPending,
  formatBriefTrackerDate,
  formatBriefTrackerNumber,
  shouldRunCompletionCheck,
  socialPlatformLabel,
  shoutBriefPlatformRows,
  videosAvailableForSubmittedPost,
  shoutBriefPlatformGroups,
  shoutBriefPlatformRowClass,
  shoutBriefPlatformBadgeClass,
  submittedPostStatusPillClass,
  submittedPostSuperSocialHandleMatch,
  superSocialAccountHandleValue,
  superSocialIdentityLabelForChannel,
  superSocialUpdatedDateColour,
} from './brief-tracker.helpers';
import {
  BriefTrackerCandidatePost,
  BriefTrackerLookupResult,
  BriefTrackerSuperSocialAccount,
} from './brief-tracker.types';
import { AdminBriefTrackerService } from '../../services/admin-brief-tracker.service';
import { AdminSettingsContextService } from '../../services/admin-settings-context.service';
import { autoLoadOnContextKey } from '../../services/admin-settings-auto-load';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { summariseBriefAsk } from '@/app/common/brief-ask/summarise-brief-ask';

@Component({
  selector: 'admin-tool-brief-tracker',
  imports: [NgOptimizedImage],
  providers: [
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => config.src,
    },
  ],
  templateUrl: './brief-tracker.html',
  styleUrl: './brief-tracker.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefTracker {
  readonly settingsContext = inject(AdminSettingsContextService);
  readonly summariseBriefAsk = summariseBriefAsk;

  searchLoading = signal(false);
  completionCheckLoading = signal(false);
  unhireLoading = signal(false);
  restoreToAppliedLoading = signal(false);
  lookupResult = signal<BriefTrackerLookupResult | null>(null);
  searchError = signal<string | null>(null);
  completionCheckNotice = signal<string | null>(null);
  submittedPostValidateLoading = signal<Record<string, boolean>>({});
  submittedPostForceValidateLoading = signal<Record<string, boolean>>({});
  submittedPostEditLoading = signal<Record<string, boolean>>({});
  submittedPostEditState = signal<
    Record<string, { videoLink: string; adCode: string }>
  >({});
  pendingResolvedFullUrl = signal<Record<string, string>>({});
  videoLinkResolveLoading = signal<Record<string, boolean>>({});
  videoLinkResolveError = signal<Record<string, string | null>>({});
  createPanelOpen = signal(false);
  createLoading = signal(false);
  createSocialChannel = signal<number | null>(null);
  createVideoId = signal<string | null>(null);
  createSelectedPostId = signal<string | null>(null);
  createManualLink = signal('');
  createAdCode = signal('');
  createCandidatesLoading = signal(false);
  createCandidatesError = signal<string | null>(null);
  createCandidatePosts = signal<BriefTrackerCandidatePost[]>([]);

  needsCompletionCheck = computed(() => {
    const result = this.lookupResult();
    return result ? shouldRunCompletionCheck(result) : false;
  });

  readonly applicationStatusLabel = applicationStatusLabel;
  readonly socialPlatformLabel = socialPlatformLabel;
  readonly shoutBriefPlatformGroups = shoutBriefPlatformGroups;
  readonly shoutBriefPlatformRowClass = shoutBriefPlatformRowClass;
  readonly shoutBriefPlatformBadgeClass = shoutBriefPlatformBadgeClass;
  readonly formatDate = formatBriefTrackerDate;
  readonly formatNumber = formatBriefTrackerNumber;
  readonly submittedPostStatusPillClass = submittedPostStatusPillClass;
  readonly canRevalidateSubmittedPost = canRevalidateSubmittedPost;
  readonly canForceValidateSubmittedPost = canForceValidateSubmittedPost;
  readonly canUnhireApplication = canUnhireApplication;
  readonly canRestoreUnhiredToApplied = canRestoreUnhiredToApplied;
  readonly canOfferCreateSubmittedPost = canOfferCreateSubmittedPost;
  readonly canCreateSubmittedPostOnChannel = canCreateSubmittedPostOnChannel;
  readonly videosAvailableForSubmittedPost = videosAvailableForSubmittedPost;
  readonly shoutBriefPlatformRows = shoutBriefPlatformRows;
  readonly isStatusHistoryUserStatus = isStatusHistoryUserStatus;
  readonly statusHistoryUserLabel = statusHistoryUserLabel;
  readonly buildSubmittedPostChecklist = buildSubmittedPostChecklist;
  readonly superSocialUpdatedDateColour = superSocialUpdatedDateColour;
  readonly superSocialAccountHandleValue = superSocialAccountHandleValue;
  readonly submittedPostSuperSocialHandleMatch = submittedPostSuperSocialHandleMatch;
  readonly superSocialIdentityLabelForChannel = superSocialIdentityLabelForChannel;

  isSuperSocialAccountMatched(
    account: BriefTrackerSuperSocialAccount,
    result: BriefTrackerLookupResult,
  ): boolean {
    return result.submittedPosts.some(
      (post) =>
        this.getSubmittedPostHandleMatch(post, result.superSocial).matchedAccountId === account.id,
    );
  }

  /** Merge saved post with draft videoLink / pending fullUrl for live handle-match preview. */
  getSubmittedPostForHandleMatch(post: SubmittedPostDto): {
    fullUrl?: string | null;
    videoLink?: string | null;
    linkHandle?: string | null;
    socialChannel: number;
  } {
    const draft = this.getSubmittedPostEditState(post);
    const draftVideoLink = draft.videoLink.trim();
    const savedVideoLink = (post.videoLink ?? '').trim();
    const hasVideoLinkEdit = draftVideoLink !== savedVideoLink;
    const pendingFullUrl = this.getPendingFullUrl(post.id);
    const isResolving = this.isVideoLinkResolveLoading(post.id);

    if (!hasVideoLinkEdit && pendingFullUrl === null && !isResolving) {
      return post;
    }

    return {
      ...post,
      videoLink: draftVideoLink || null,
      fullUrl:
        pendingFullUrl ??
        (hasVideoLinkEdit || isResolving ? null : (post.fullUrl ?? null)),
    };
  }

  getSubmittedPostHandleMatch(
    post: SubmittedPostDto,
    profile: BriefTrackerLookupResult['superSocial'],
  ) {
    return submittedPostSuperSocialHandleMatch(this.getSubmittedPostForHandleMatch(post), profile);
  }

  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private trackerService = inject(AdminBriefTrackerService);
  private toastNotificationService = inject(ToastNotificationService);
  private alertService = inject(AlertService);
  private firebaseService = inject(FirebaseService);
  private submittedPostPollSubscriptions = new Map<string, Subscription>();
  private createCandidatesSubscription: Subscription | null = null;
  private videoLinkResolveTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private static readonly VIDEO_LINK_RESOLVE_DEBOUNCE_MS = 600;

  constructor() {
    autoLoadOnContextKey(() => {
      const briefId = this.settingsContext.briefId().trim();
      const creatorId = this.settingsContext.creatorId().trim();
      return briefId && creatorId ? `${briefId}|${creatorId}` : null;
    }, () => this.runSearch());

    this.destroyRef.onDestroy(() => {
      for (const sub of this.submittedPostPollSubscriptions.values()) {
        sub.unsubscribe();
      }
      this.submittedPostPollSubscriptions.clear();
      for (const timer of this.videoLinkResolveTimers.values()) {
        clearTimeout(timer);
      }
      this.videoLinkResolveTimers.clear();
      this.createCandidatesSubscription?.unsubscribe();
    });
  }

  runSearch(): void {
    const briefId = this.settingsContext.briefId().trim();
    const creatorId = this.settingsContext.creatorId().trim();

    if (!briefId || !creatorId) {
      void this.toastNotificationService.alert({
        title: 'Missing fields',
        message: 'Enter both brief ID and creator Firebase UID.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.searchLoading.set(true);
    this.searchError.set(null);
    this.completionCheckNotice.set(null);
    this.lookupResult.set(null);
    this.submittedPostValidateLoading.set({});
    this.submittedPostForceValidateLoading.set({});
    this.submittedPostEditLoading.set({});
    this.submittedPostEditState.set({});
    this.pendingResolvedFullUrl.set({});
    this.videoLinkResolveLoading.set({});
    this.videoLinkResolveError.set({});
    this.resetCreatePanel();

    this.trackerService
      .lookup(briefId, creatorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.searchLoading.set(false);
          this.lookupResult.set(result);
          if (shouldRunCompletionCheck(result)) {
            this.runCompletionCheck(result, true);
          }
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.searchLoading.set(false);
          const message = this.extractErrorMessage(err);
          this.searchError.set(message);
          void this.toastNotificationService.alert({
            title: 'Lookup failed',
            message,
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  isSubmittedPostValidateLoading(postId: string): boolean {
    return this.submittedPostValidateLoading()[postId] === true;
  }

  isSubmittedPostForceValidateLoading(postId: string): boolean {
    return this.submittedPostForceValidateLoading()[postId] === true;
  }

  isSubmittedPostEditLoading(postId: string): boolean {
    return this.submittedPostEditLoading()[postId] === true;
  }

  isVideoLinkResolveLoading(postId: string): boolean {
    return this.videoLinkResolveLoading()[postId] === true;
  }

  getVideoLinkResolveError(postId: string): string | null {
    return this.videoLinkResolveError()[postId] ?? null;
  }

  getSubmittedPostEditState(post: SubmittedPostDto): {
    videoLink: string;
    adCode: string;
  } {
    return (
      this.submittedPostEditState()[post.id] ?? {
        videoLink: post.videoLink ?? '',
        adCode: post.adCode ?? '',
      }
    );
  }

  getSavedFullUrl(post: SubmittedPostDto): string {
    return post.fullUrl?.trim() ?? '';
  }

  getPendingFullUrl(postId: string): string | null {
    return this.pendingResolvedFullUrl()[postId] ?? null;
  }

  hasUnsavedFullUrlPreview(post: SubmittedPostDto): boolean {
    const pending = this.getPendingFullUrl(post.id);
    return pending !== null && pending !== this.getSavedFullUrl(post);
  }

  getDisplayedFullUrl(post: SubmittedPostDto): string {
    return this.getPendingFullUrl(post.id) ?? this.getSavedFullUrl(post);
  }

  updateSubmittedPostField(
    post: SubmittedPostDto,
    field: 'videoLink' | 'adCode',
    value: string,
  ): void {
    const current = this.getSubmittedPostEditState(post);
    this.submittedPostEditState.update((state) => ({
      ...state,
      [post.id]: {
        ...current,
        [field]: value,
      },
    }));
  }

  onVideoLinkInput(post: SubmittedPostDto, value: string): void {
    this.updateSubmittedPostField(post, 'videoLink', value);

    const existingTimer = this.videoLinkResolveTimers.get(post.id);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const trimmed = value.trim();
    if (!trimmed) {
      this.videoLinkResolveLoading.update((state) => {
        const next = { ...state };
        delete next[post.id];
        return next;
      });
      this.pendingResolvedFullUrl.update((state) => {
        const next = { ...state };
        delete next[post.id];
        return next;
      });
      this.videoLinkResolveError.update((state) => ({ ...state, [post.id]: null }));
      this.cdr.markForCheck();
      return;
    }

    const savedVideoLink = (post.videoLink ?? '').trim();
    if (trimmed === savedVideoLink) {
      this.pendingResolvedFullUrl.update((state) => {
        const next = { ...state };
        delete next[post.id];
        return next;
      });
      this.videoLinkResolveLoading.update((state) => {
        const next = { ...state };
        delete next[post.id];
        return next;
      });
      this.videoLinkResolveError.update((state) => ({ ...state, [post.id]: null }));
      this.cdr.markForCheck();
      return;
    }

    this.pendingResolvedFullUrl.update((state) => {
      const next = { ...state };
      delete next[post.id];
      return next;
    });
    this.videoLinkResolveLoading.update((state) => ({ ...state, [post.id]: true }));
    this.videoLinkResolveError.update((state) => ({ ...state, [post.id]: null }));
    this.cdr.markForCheck();

    const timer = setTimeout(() => {
      this.videoLinkResolveTimers.delete(post.id);
      this.videoLinkResolveLoading.update((state) => ({ ...state, [post.id]: true }));
      this.videoLinkResolveError.update((state) => ({ ...state, [post.id]: null }));
      this.cdr.markForCheck();

      this.trackerService
        .resolveVideoLinkForTracker(trimmed, post.socialChannel)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (resolved) => {
            this.videoLinkResolveLoading.update((state) => {
              const next = { ...state };
              delete next[post.id];
              return next;
            });
            this.pendingResolvedFullUrl.update((state) => ({
              ...state,
              [post.id]: resolved.fullUrl,
            }));
            this.videoLinkResolveError.update((state) => ({ ...state, [post.id]: null }));
            this.cdr.markForCheck();
          },
          error: (err: { error?: { message?: string | string[] }; message?: string }) => {
            this.videoLinkResolveLoading.update((state) => {
              const next = { ...state };
              delete next[post.id];
              return next;
            });
            this.videoLinkResolveError.update((state) => ({
              ...state,
              [post.id]: this.extractErrorMessage(err),
            }));
            this.cdr.markForCheck();
          },
        });
    }, BriefTracker.VIDEO_LINK_RESOLVE_DEBOUNCE_MS);

    this.videoLinkResolveTimers.set(post.id, timer);
  }

  saveSubmittedPostEdits(post: SubmittedPostDto): void {
    const current = this.lookupResult();
    if (!current) {
      return;
    }

    const draft = this.getSubmittedPostEditState(post);
    const videoLink = draft.videoLink.trim();
    const adCode = draft.adCode.trim();
    const pendingFullUrl = this.getPendingFullUrl(post.id)?.trim() ?? '';
    const patch: Pick<SubmittedPostDto, 'videoLink' | 'adCode' | 'fullUrl'> = {
      videoLink,
      adCode,
    };
    if (pendingFullUrl) {
      patch.fullUrl = pendingFullUrl;
    }

    this.submittedPostEditLoading.update((state) => ({ ...state, [post.id]: true }));
    this.cdr.markForCheck();

    this.trackerService
      .updateSubmittedPostForTracker(post.id, patch)
      .pipe(
        switchMap(() => this.trackerService.lookup(current.briefId, current.creatorId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (refreshed) => {
          this.submittedPostEditLoading.update((state) => {
            const next = { ...state };
            delete next[post.id];
            return next;
          });

          this.lookupResult.set(refreshed);

          const updatedPost =
            refreshed.submittedPosts.find((row) => row.id === post.id) ?? {
              ...post,
              videoLink,
              adCode,
              fullUrl: pendingFullUrl || post.fullUrl,
            };

          this.submittedPostEditState.update((state) => ({
            ...state,
            [post.id]: {
              videoLink: updatedPost.videoLink ?? '',
              adCode: updatedPost.adCode ?? '',
            },
          }));
          this.pendingResolvedFullUrl.update((state) => {
            const next = { ...state };
            delete next[post.id];
            return next;
          });

          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Submitted post updated',
            message: 'Saved fullUrl, videoLink, and adCode changes.',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.submittedPostEditLoading.update((state) => {
            const next = { ...state };
            delete next[post.id];
            return next;
          });
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Update failed',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  async forceValidateSubmittedPost(post: SubmittedPostDto): Promise<void> {
    const result = this.lookupResult();
    const applicationId = result?.application?.id;
    const applicationStatus = result?.application?.applicationStatus;
    if (
      !applicationId ||
      !result ||
      !canForceValidateSubmittedPost(post.status, applicationStatus)
    ) {
      return;
    }

    const confirmed = await this.alertService.confirm({
      title: 'Force validate post?',
      message:
        'This marks the submitted post as Valid without running SuperSocial checks. Primary posts then run the completion check; extra-platform posts queue the extra payment.',
      confirmText: 'Force validate',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'error',
    });

    if (!confirmed) {
      return;
    }

    const postId = post.id;
    this.submittedPostForceValidateLoading.update((state) => ({ ...state, [postId]: true }));
    this.cdr.markForCheck();

    this.trackerService
      .forceValidateSubmittedPostForTracker(postId, applicationId, applicationStatus)
      .pipe(
        switchMap(() => this.trackerService.lookup(result.briefId, result.creatorId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (refreshed) => {
          this.submittedPostForceValidateLoading.update((state) => {
            const next = { ...state };
            delete next[postId];
            return next;
          });
          this.lookupResult.set(refreshed);
          this.cdr.markForCheck();

          const newStatus = refreshed.application?.applicationStatus;
          const updatedPost = refreshed.submittedPosts.find((row) => row.id === postId);
          if (updatedPost?.status === SubmittedPostStatusEnum.Valid) {
            if (applicationStatus === ApplicationStatusEnum.ReadyToPost) {
              if (newStatus != null && newStatus !== ApplicationStatusEnum.ReadyToPost) {
                this.completionCheckNotice.set(
                  `Force validate finished — status is now ${applicationStatusLabel(newStatus)} (${newStatus}).`,
                );
                void this.toastNotificationService.alert({
                  title: 'Post force validated',
                  message: `Post is Valid. Application is now ${applicationStatusLabel(newStatus)} (${newStatus}).`,
                  type: 'success',
                  icon: 'success',
                });
              } else {
                this.completionCheckNotice.set(
                  'Post is Valid and completion check ran, but status is still ReadyToPost. Ensure valid posts match brief socialPlatform and videosPerCreator is met.',
                );
                void this.toastNotificationService.alert({
                  title: 'Post force validated',
                  message:
                    'Post is Valid. Completion check ran but application is still ReadyToPost — verify socialChannel matches brief platform and videosPerCreator is met.',
                  type: 'warning',
                  icon: 'warning',
                });
              }
            } else {
              this.completionCheckNotice.set(
                'Post is Valid. Extra-platform payment is queued when this post is not the brief primary channel.',
              );
              void this.toastNotificationService.alert({
                title: 'Post force validated',
                message: 'Post is Valid. Application status is unchanged.',
                type: 'success',
                icon: 'success',
              });
            }
          } else {
            void this.toastNotificationService.alert({
              title: 'Force validate finished',
              message: `Post status is "${updatedPost?.status ?? 'unknown'}".`,
              type: 'warning',
              icon: 'warning',
            });
          }
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.submittedPostForceValidateLoading.update((state) => {
            const next = { ...state };
            delete next[postId];
            return next;
          });
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Force validate failed',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  revalidateSubmittedPost(post: SubmittedPostDto): void {
    const result = this.lookupResult();
    const applicationId = result?.application?.id;
    if (!applicationId || !canRevalidateSubmittedPost(post.status)) {
      return;
    }

    const postId = post.id;
    this.stopSubmittedPostValidationPoll(postId);
    this.submittedPostValidateLoading.update((state) => ({ ...state, [postId]: true }));
    this.cdr.markForCheck();

    // Poll in parallel — deferred validation can take minutes; don't wait for HTTP to finish.
    this.startSubmittedPostValidationPoll(postId, applicationId);

    this.trackerService
      .validateSubmittedPostForTracker(postId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          void this.toastNotificationService.alert({
            title: 'Validation started',
            message: 'Re-validating submitted post — watching for status update…',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.stopSubmittedPostValidationPoll(postId);
          this.clearSubmittedPostValidateLoading(postId);
          void this.toastNotificationService.alert({
            title: 'Validation failed',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  private startSubmittedPostValidationPoll(postId: string, applicationId: string): void {
    this.stopSubmittedPostValidationPoll(postId);

    const pollIntervalMs = 3000;
    const maxAttempts = 40;
    let completionCheckTriggered = false;
    let lastResult: { posts: SubmittedPostDto[]; post: SubmittedPostDto | null } | null = null;

    const sub = timer(0, pollIntervalMs)
      .pipe(
        take(maxAttempts),
        switchMap(() =>
          this.trackerService.getSubmittedPostsForApplication(applicationId).pipe(
            map((posts) => ({
              posts,
              post: posts.find((row) => row.id === postId) ?? null,
            })),
          ),
        ),
        tap(({ posts }) => {
          this.applySubmittedPosts(posts);
          this.cdr.markForCheck();
        }),
        takeWhile(
          ({ post }) => post != null && isSubmittedPostValidationPending(post.status),
          true,
        ),
        finalize(() => {
          this.clearSubmittedPostValidateLoading(postId);
          this.submittedPostPollSubscriptions.delete(postId);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (result) => {
          lastResult = result;
          if (
            result.post?.status === SubmittedPostStatusEnum.Valid &&
            !completionCheckTriggered
          ) {
            completionCheckTriggered = true;
            void this.toastNotificationService.alert({
              title: 'Post validated',
              message: 'Submitted post is now Valid. Running completion check…',
              type: 'success',
              icon: 'success',
            });
            const refreshed = this.lookupResult();
            if (refreshed && shouldRunCompletionCheck(refreshed)) {
              this.runCompletionCheck(refreshed, false);
            }
          }
        },
        complete: () => {
          if (completionCheckTriggered || !lastResult?.post) {
            if (!lastResult?.post) {
              void this.toastNotificationService.alert({
                title: 'Validation finished',
                message: 'Could not find the submitted post after validation.',
                type: 'warning',
                icon: 'warning',
              });
            }
            return;
          }

          void this.toastNotificationService.alert({
            title: 'Validation finished',
            message: isSubmittedPostValidationPending(lastResult.post.status)
              ? `Post is still "${lastResult.post.status}" after polling. Validation may still be running — try Re-lookup shortly.`
              : `Post status is "${lastResult.post.status}" (not Valid). Check hashtags, account connection, or SuperSocial data.`,
            type: 'warning',
            icon: 'warning',
          });
        },
        error: () => {
          void this.toastNotificationService.alert({
            title: 'Validation poll failed',
            message: 'Could not refresh submitted post status. Try Re-lookup.',
            type: 'error',
            icon: 'error',
          });
        },
      });

    this.submittedPostPollSubscriptions.set(postId, sub);
  }

  private stopSubmittedPostValidationPoll(postId: string): void {
    const sub = this.submittedPostPollSubscriptions.get(postId);
    if (sub) {
      sub.unsubscribe();
      this.submittedPostPollSubscriptions.delete(postId);
    }
  }

  private clearSubmittedPostValidateLoading(postId: string, markForCheck = true): void {
    this.submittedPostValidateLoading.update((state) => {
      if (!state[postId]) {
        return state;
      }
      const next = { ...state };
      delete next[postId];
      return next;
    });
    if (markForCheck) {
      this.cdr.markForCheck();
    }
  }

  private applySubmittedPosts(posts: SubmittedPostDto[]): void {
    const current = this.lookupResult();
    if (!current) {
      return;
    }
    this.lookupResult.set({
      ...current,
      submittedPosts: posts,
      submittedPostsFound: posts.length > 0,
    });
  }

  openCreateSubmittedPostPanel(): void {
    const result = this.lookupResult();
    const primary = result?.shoutBrief?.socialPlatform;
    if (!result || primary == null || !canOfferCreateSubmittedPost(
      result.application?.applicationStatus,
      result.shoutBrief,
      result.videoChecklist.videos,
      result.submittedPosts,
    )) {
      return;
    }

    this.createPanelOpen.set(true);
    this.createSocialChannel.set(Number(primary));
    this.createManualLink.set('');
    this.createAdCode.set('');
    this.createSelectedPostId.set(null);
    this.selectFirstAvailableVideo();
    this.loadCreateCandidatePosts();
  }

  closeCreateSubmittedPostPanel(): void {
    this.resetCreatePanel();
  }

  onCreateSocialChannelChange(value: string): void {
    const channel = Number(value);
    if (!Number.isFinite(channel)) {
      return;
    }
    this.createSocialChannel.set(channel);
    this.createSelectedPostId.set(null);
    this.createManualLink.set('');
    this.selectFirstAvailableVideo();
    this.loadCreateCandidatePosts();
  }

  onCreateVideoChange(value: string): void {
    this.createVideoId.set(value || null);
  }

  onCreateManualLinkInput(value: string): void {
    this.createManualLink.set(value);
    if (value.trim()) {
      this.createSelectedPostId.set(null);
    }
  }

  onCreateAdCodeInput(value: string): void {
    this.createAdCode.set(value);
  }

  selectCreateCandidatePost(postId: string): void {
    this.createSelectedPostId.set(postId);
    this.createManualLink.set('');
  }

  createChannelChoices() {
    const result = this.lookupResult();
    if (!result?.shoutBrief) {
      return [];
    }
    return shoutBriefPlatformRows(result.shoutBrief).filter((row) =>
      canCreateSubmittedPostOnChannel(
        result.application?.applicationStatus,
        row.id,
        result.shoutBrief?.socialPlatform,
      ),
    );
  }

  createVideoChoices() {
    const result = this.lookupResult();
    const channel = this.createSocialChannel();
    if (!result || channel == null) {
      return [];
    }
    return videosAvailableForSubmittedPost(
      result.videoChecklist.videos,
      result.submittedPosts,
      channel,
    );
  }

  async submitCreateSubmittedPost(): Promise<void> {
    const result = this.lookupResult();
    const applicationId = result?.application?.id;
    const channel = this.createSocialChannel();
    const videoId = this.createVideoId();
    if (!result || !applicationId || channel == null || !videoId) {
      return;
    }

    const selected = this.createCandidatePosts().find((post) => post.id === this.createSelectedPostId());
    const manualLink = this.createManualLink().trim();
    const videoLink = manualLink || selected?.shareUrl || '';
    if (!videoLink) {
      void this.toastNotificationService.alert({
        title: 'Choose a post',
        message: 'Select a Super Social post or paste a post URL.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const confirmed = await this.alertService.confirm({
      title: 'Create submitted post?',
      message:
        'This attaches the social post to the approved video and queues validation. When the required posts are Valid, the application moves to Posted and the hire payout runs.',
      confirmText: 'Create and validate',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });
    if (!confirmed) {
      return;
    }

    const postId =
      !manualLink && selected && channel === SocialChannelEnum['TikTok Post'] ? selected.id : undefined;

    this.createLoading.set(true);
    this.cdr.markForCheck();

    this.trackerService
      .createSubmittedPostForTracker({
        briefApplicationId: applicationId,
        videoId,
        socialChannel: channel,
        videoLink,
        adCode: this.createAdCode().trim(),
        ...(postId ? { postId } : {}),
      })
      .pipe(
        switchMap((created) =>
          this.trackerService.validateSubmittedPostForTracker(created.id).pipe(
            map(() => created),
          ),
        ),
        switchMap((created) =>
          this.trackerService.lookup(result.briefId, result.creatorId).pipe(
            map((refreshed) => ({ created, refreshed })),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ created, refreshed }) => {
          this.createLoading.set(false);
          this.lookupResult.set(refreshed);
          this.resetCreatePanel();
          this.startSubmittedPostValidationPoll(created.id, applicationId);
          this.submittedPostValidateLoading.update((state) => ({ ...state, [created.id]: true }));
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Submitted post created',
            message: 'Validation is running. The application moves to Posted once the required posts are Valid.',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.createLoading.set(false);
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Could not create submitted post',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  private selectFirstAvailableVideo(): void {
    const first = this.createVideoChoices()[0];
    this.createVideoId.set(first?.id ?? null);
  }

  private loadCreateCandidatePosts(): void {
    const result = this.lookupResult();
    const channel = this.createSocialChannel();
    if (!result || channel == null) {
      this.createCandidatePosts.set([]);
      return;
    }

    this.createCandidatesSubscription?.unsubscribe();
    this.createCandidatesLoading.set(true);
    this.createCandidatesError.set(null);
    this.createCandidatePosts.set([]);
    this.cdr.markForCheck();

    this.createCandidatesSubscription = this.trackerService
      .loadCandidatePosts(channel, result.superSocial)
      .subscribe({
        next: (posts) => {
          this.createCandidatesLoading.set(false);
          this.createCandidatePosts.set(posts);
          this.cdr.markForCheck();
        },
        error: () => {
          this.createCandidatesLoading.set(false);
          this.createCandidatesError.set('Could not load Super Social posts.');
          this.cdr.markForCheck();
        },
      });
  }

  private resetCreatePanel(): void {
    this.createCandidatesSubscription?.unsubscribe();
    this.createCandidatesSubscription = null;
    this.createPanelOpen.set(false);
    this.createLoading.set(false);
    this.createSocialChannel.set(null);
    this.createVideoId.set(null);
    this.createSelectedPostId.set(null);
    this.createManualLink.set('');
    this.createAdCode.set('');
    this.createCandidatesLoading.set(false);
    this.createCandidatesError.set(null);
    this.createCandidatePosts.set([]);
  }

  runCompletionCheckManually(): void {
    const result = this.lookupResult();
    if (!result?.application?.id || !shouldRunCompletionCheck(result)) {
      return;
    }
    this.runCompletionCheck(result, false);
  }

  async unhireApplication(): Promise<void> {
    const result = this.lookupResult();
    const application = result?.application;
    if (
      !result ||
      !application?.id ||
      !canUnhireApplication(application.applicationStatus, true)
    ) {
      return;
    }

    const previousStatus = application.applicationStatus;
    const briefId = result.briefId;
    const creatorId = result.creatorId;
    const confirmed = await this.alertService.confirm({
      title: 'Un-hire creator?',
      message:
        `This sets application ${application.id} from ${applicationStatusLabel(previousStatus)} (${previousStatus}) to Hired removed (${ApplicationStatusEnum.HiredRemoved}), releases the wallet reservation, and records a status history entry. This is not ApplicationDeclined.`,
      confirmText: 'Un-hire',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });

    if (!confirmed) {
      return;
    }

    const userId = this.firebaseService.userId;
    if (!userId) {
      void this.toastNotificationService.alert({
        title: 'Un-hire failed',
        message: 'Could not determine the current admin user.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.unhireLoading.set(true);
    this.cdr.markForCheck();

    this.trackerService
      .unhireApplicationForTracker(application.id, userId)
      .pipe(
        switchMap(() => this.trackerService.lookup(briefId, creatorId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (refreshed: BriefTrackerLookupResult) => {
          this.unhireLoading.set(false);
          this.lookupResult.set(refreshed);
          this.cdr.markForCheck();
          const newStatus = refreshed.application?.applicationStatus;
          void this.toastNotificationService.alert({
            title: 'Creator un-hired',
            message:
              newStatus != null
                ? `Status is now ${applicationStatusLabel(newStatus)} (${newStatus}).`
                : `Moved from ${applicationStatusLabel(previousStatus)} to Hired removed.`,
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.unhireLoading.set(false);
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Un-hire failed',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  async restoreUnhiredToApplied(): Promise<void> {
    const result = this.lookupResult();
    const application = result?.application;
    if (
      !result ||
      !application?.id ||
      !canRestoreUnhiredToApplied(application.applicationStatus)
    ) {
      return;
    }

    const briefId = result.briefId;
    const creatorId = result.creatorId;
    const confirmed = await this.alertService.confirm({
      title: 'Move back to Applied?',
      message:
        `This sets application ${application.id} from Un-hired (${ApplicationStatusEnum.HiredRemoved}) to Applied (${ApplicationStatusEnum.Applied}) and clears the decline reason. A live wallet reservation is released so a later hire does not charge twice. The creator is not notified and is not hired.`,
      confirmText: 'Move back to Applied',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });

    if (!confirmed) {
      return;
    }

    this.restoreToAppliedLoading.set(true);
    this.cdr.markForCheck();

    this.trackerService
      .restoreUnhiredToApplied(application.id)
      .pipe(
        switchMap(() => this.trackerService.lookup(briefId, creatorId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (refreshed: BriefTrackerLookupResult) => {
          this.restoreToAppliedLoading.set(false);
          this.lookupResult.set(refreshed);
          this.cdr.markForCheck();
          const newStatus = refreshed.application?.applicationStatus;
          void this.toastNotificationService.alert({
            title: 'Moved back to Applied',
            message:
              newStatus != null
                ? `Status is now ${applicationStatusLabel(newStatus)} (${newStatus}). The brand can hire this creator again.`
                : 'Status is now Applied. The brand can hire this creator again.',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.restoreToAppliedLoading.set(false);
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Could not move back to Applied',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  private runCompletionCheck(result: BriefTrackerLookupResult, automatic: boolean): void {
    const applicationId = result.application!.id;
    const previousStatus = result.application!.applicationStatus;

    this.completionCheckLoading.set(true);
    this.completionCheckNotice.set(
      automatic
        ? 'Posted history found while status is still ReadyToPost — running completion check…'
        : 'Running completion check…',
    );

    this.trackerService
      .runCompletionCheck(applicationId)
      .pipe(
        switchMap(() => this.trackerService.lookup(result.briefId, result.creatorId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (refreshed) => {
          this.completionCheckLoading.set(false);
          this.lookupResult.set(refreshed);

          const newStatus = refreshed.application?.applicationStatus;
          if (newStatus != null && newStatus !== ApplicationStatusEnum.ReadyToPost) {
            this.completionCheckNotice.set(
              `Completion check finished — status is now ${applicationStatusLabel(newStatus)} (${newStatus}).`,
            );
            void this.toastNotificationService.alert({
              title: 'Completion check succeeded',
              message: `Application advanced from ReadyToPost (${previousStatus}) to ${applicationStatusLabel(newStatus)} (${newStatus}).`,
              type: 'success',
              icon: 'success',
            });
          } else {
            this.completionCheckNotice.set(
              'Completion check ran but status is still ReadyToPost. Re-validate submitted posts until status is Valid, or ensure videosPerCreator is met.',
            );
            void this.toastNotificationService.alert({
              title: automatic ? 'Completion check ran' : 'Completion check complete',
              message:
                'Status unchanged (still ReadyToPost). Use Re-validate post on each non-Valid submitted post, then re-run completion check.',
              type: 'warning',
              icon: 'warning',
            });
          }
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.completionCheckLoading.set(false);
          const message = this.extractErrorMessage(err);
          this.completionCheckNotice.set(`Completion check failed: ${message}`);
          void this.toastNotificationService.alert({
            title: 'Completion check failed',
            message,
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  private extractErrorMessage(err: {
    error?: { message?: string | string[] };
    message?: string;
  }): string {
    const nested = err?.error?.message;
    if (Array.isArray(nested)) {
      return nested.join(', ');
    }
    if (typeof nested === 'string' && nested.length) {
      return nested;
    }
    return err?.message ?? 'Request failed';
  }
}
